import React, { useState, useEffect, useCallback } from 'react';
import VideoPlayer from './VideoPlayer';
import axios from 'axios';
import '../styles/VideoLibrary.css';

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('title');
  const [filterByPoints, setFilterByPoints] = useState('all');
  const [testResults, setTestResults] = useState({});

  // Completely independent fetch function that bypasses ALL mobile optimizations
  const fetchAllVideos = useCallback(async () => {
    const API_URL = process.env.REACT_APP_API_URL || '';
    
    try {
      console.log('📚 Admin page: Fetching ALL videos with completely independent API call...');
      
      // Use the maximum allowed limit by the backend (50) and fetch all videos with pagination
      console.log('📚 Backend allows maximum 50 videos per request, fetching all videos with pagination...');
      
      let allVideos = [];
      let cursor = null;
      let hasMore = true;
      
      while (hasMore) {
        try {
          console.log(`📚 Fetching batch with cursor: ${cursor || 'none'}`);
          
          const response = await axios.get(`${API_URL}/api/videos`, {
            params: { 
              limit: 50, // Use maximum allowed limit
              cursor: cursor
            },
            timeout: 30000,
            headers: {
              'X-Admin-Request': 'true',
              'X-Bypass-Mobile-Limits': 'true'
            }
          });
          
          const batchVideos = response.data?.videos || [];
          const nextCursor = response.data?.next_cursor;
          
          console.log(`📚 Received ${batchVideos.length} videos in this batch`);
          console.log(`📚 Next cursor: ${nextCursor || 'none'}`);
          
          allVideos = [...allVideos, ...batchVideos];
          
          // Check if there are more videos
          if (nextCursor && batchVideos.length > 0) {
            cursor = nextCursor;
          } else {
            hasMore = false;
          }
          
          // Safety check to prevent infinite loops
          if (allVideos.length > 200) {
            console.log('📚 Safety limit reached, stopping pagination');
            hasMore = false;
          }
          
        } catch (error) {
          console.error(`❌ Error fetching batch: ${error.message}`);
          hasMore = false;
        }
      }
      
      console.log(`📚 Pagination complete! Total videos fetched: ${allVideos.length}`);
      
      return {
        videos: allVideos,
        next_cursor: null
      };
      
    } catch (error) {
      console.error('❌ All fetch attempts failed:', error);
      throw error;
    }
  }, []); // No dependencies needed

  const loadVideos = useCallback(async () => {
    try {
      setLoading(true);
      const videoData = await fetchAllVideos();
      
      // Debug logging to see what we actually received
      console.log('🔍 DEBUG: Raw API response:', videoData);
      console.log('🔍 DEBUG: Videos array:', videoData.videos);
      console.log('🔍 DEBUG: Videos count:', videoData.videos?.length);
      console.log('🔍 DEBUG: First few videos:', videoData.videos?.slice(0, 5));
      
      setVideos(videoData.videos || []);
      setError(null);
      console.log(`📚 Successfully loaded ${videoData.videos?.length || 0} videos for admin library`);
    } catch (err) {
      console.error('Failed to load videos:', err);
      setError('Failed to load video data');
    } finally {
      setLoading(false);
    }
  }, [fetchAllVideos]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    // Clear previous test result for this video
    setTestResults(prev => ({
      ...prev,
      [video.filename]: null
    }));
  };

  const handleVideoTestResult = (video, result) => {
    setTestResults(prev => ({
      ...prev,
      [video.filename]: {
        ...result,
        timestamp: new Date().toISOString()
      }
    }));
  };

  const closeVideoPlayer = () => {
    setSelectedVideo(null);
  };

  // Filter and sort videos
  const filteredAndSortedVideos = videos
    .filter(video => {
      // Safely handle potentially undefined properties
      const title = video.title || '';
      const description = video.description || '';
      const filename = video.filename || '';
      const points = video.points || 0;
      
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           filename.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPointsFilter = filterByPoints === 'all' ||
        (filterByPoints === 'low' && points <= 10) ||
        (filterByPoints === 'medium' && points > 10 && points <= 20) ||
        (filterByPoints === 'high' && points > 20);
      
      return matchesSearch && matchesPointsFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'points':
          return (b.points || 0) - (a.points || 0);
        case 'filename':
          return (a.filename || '').localeCompare(b.filename || '');
        default:
          return 0;
      }
    });

  const getTestResultIcon = (video) => {
    const result = testResults[video.filename];
    if (!result) return '⚪'; // Not tested
    if (result.success) return '✅'; // Success
    if (result.error) {
      if (result.error.includes('HTTP2')) return '🔴'; // HTTP2 error
      if (result.error.includes('NETWORK')) return '🟠'; // Network error
      return '❌'; // Other error
    }
    return '⚪';
  };

  const getTestResultTooltip = (video) => {
    const result = testResults[video.filename];
    if (!result) return 'Not tested yet';
    if (result.success) return `✅ Success - Played successfully at ${new Date(result.timestamp).toLocaleTimeString()}`;
    if (result.error) {
      return `❌ Error: ${result.error} at ${new Date(result.timestamp).toLocaleTimeString()}`;
    }
    return 'Unknown result';
  };

  if (loading) {
    return (
      <div className="video-library">
        <div className="library-header">
          <h1>📚 Video Library</h1>
          <p>Loading video data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-library">
        <div className="library-header">
          <h1>📚 Video Library</h1>
          <div className="error-message">
            <p>❌ {error}</p>
            <button onClick={loadVideos} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-library">
      <div className="library-header">
        <h1>📚 Video Library</h1>
        <p>Browse and test individual videos from the complete video collection</p>
        
        <div className="library-controls">
          <div className="search-control">
            <input
              type="text"
              placeholder="Search videos by title, description, or filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="title">Sort by Title</option>
              <option value="points">Sort by Points</option>
              <option value="filename">Sort by Filename</option>
            </select>
            
            <select
              value={filterByPoints}
              onChange={(e) => setFilterByPoints(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Points</option>
              <option value="low">Low Points (≤10)</option>
              <option value="medium">Medium Points (11-20)</option>
              <option value="high">High Points (&gt;20)</option>
            </select>
          </div>
        </div>

        <div className="stats-summary">
          <span>📊 Total Videos: {videos.length}</span>
          <span>🔍 Filtered: {filteredAndSortedVideos.length}</span>
          <span>✅ Tested: {Object.keys(testResults).filter(key => testResults[key]).length}</span>
          <span>🔴 Errors: {Object.keys(testResults).filter(key => testResults[key]?.error).length}</span>
        </div>
      </div>

      <div className="video-grid">
        {filteredAndSortedVideos.map((video) => (
          <div key={video.filename} className="video-card">
            <div className="video-card-header">
              <h3 className="video-title">{video.title}</h3>
              <div className="test-result-indicator" title={getTestResultTooltip(video)}>
                {getTestResultIcon(video)}
              </div>
            </div>
            
            <div className="video-info">
              <p className="video-description">{video.description}</p>
              <div className="video-meta">
                <span className="video-filename">📁 {video.filename}</span>
                <span className="video-points">💰 {video.points} points</span>
              </div>
              
              {video.url && (
                <a 
                  href={video.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="video-url"
                >
                  🔗 Visit Website
                </a>
              )}
            </div>
            
            <button
              onClick={() => handleVideoSelect(video)}
              className="play-video-button"
            >
              🎬 Play Video
            </button>
            
            {testResults[video.filename] && (
              <div className={`test-result ${testResults[video.filename].success ? 'success' : 'error'}`}>
                {testResults[video.filename].success ? (
                  <span>✅ Video played successfully</span>
                ) : (
                  <span>❌ {testResults[video.filename].error}</span>
                )}
                <small>
                  {new Date(testResults[video.filename].timestamp).toLocaleTimeString()}
                </small>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedVideo && (
        <div className="video-player-modal">
          <div className="modal-backdrop" onClick={closeVideoPlayer}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h2>🎬 Playing: {selectedVideo.title}</h2>
              <button onClick={closeVideoPlayer} className="close-button">
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="video-test-info">
                <p><strong>Filename:</strong> {selectedVideo.filename}</p>
                <p><strong>Points:</strong> {selectedVideo.points}</p>
                <p><strong>Description:</strong> {selectedVideo.description}</p>
              </div>
              
              <VideoPlayer
                video={selectedVideo}
                warmLevel="auto"
                onVideoEnd={() => {
                  handleVideoTestResult(selectedVideo, { success: true });
                }}
                onVideoError={(error) => {
                  handleVideoTestResult(selectedVideo, { 
                    success: false, 
                    error: error.message || 'Unknown error' 
                  });
                }}
                isDebugMode={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;
